import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
import DatePicker from "react-datepicker";
// components
import { Card } from "../../../../components";
// styles
import TierBudgetStyles from "./TierBudgetStyles";
// config
import { tierBudgetConfig } from "./tierBudget.config";
// services
import * as dashboardService from "../../../../services/api/hr/dashboard.service";
import { formatDateForSearch } from "../dashboad.services";
import { useTranslation } from "react-i18next";

const TierBudget = ({ tierBudgetLoading, setTierBudgetLoading }) => {
  const { t } = useTranslation();
  const [graphData, setGraphData] = useState(tierBudgetConfig);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [date, setDate] = useState(new Date());
  const [searchDateString, setSearchDateString] = useState(
    formatDateForSearch(new Date())
  );
  const isMounted = useRef(false);

  const handleDateChange = chosenDate => {
    setDate(chosenDate);
    setSearchDateString(formatDateForSearch(chosenDate));
  };

  const fetchData = async () => {
    const response = await dashboardService.fetchBudgetPerTiers(
      searchDateString
    );

    if (response.hasError) {
      return toast.error(
        response.errorMessage
          ? t(response.errorMessage)
          : t("failed_to_get_tier_budgets")
      );
    }

    if (isMounted.current) {
      setTierBudgetLoading(false);
    }

    let chosenCategoriesNames = [];
    let chosenMonthBudget = { name: t("chosen_month_budget"), data: [] };
    let previousMonthBudget = { name: t("previous_month_budget"), data: [] };

    if (
      isMounted.current &&
      response &&
      response.actualBudget &&
      response.lastBudget
    ) {
      if (response.actualBudget.length === response.lastBudget.length) {
        response.actualBudget.forEach(item => {
          chosenCategoriesNames.push(item.name);
          chosenMonthBudget.data.push(item.spentBudget);
        });
        response.lastBudget.forEach(item => {
          previousMonthBudget.data.push(item.spentBudget);
        });
      }
    } else {
      chosenCategoriesNames = [];
      chosenMonthBudget = { name: t("chosen_month_budget"), data: [] };
      previousMonthBudget = { name: t("previous_month_budget"), data: [] };
    }

    if (
      isMounted.current &&
      errorMessage !== undefined &&
      chosenCategoriesNames.length > 0
    ) {
      setErrorMessage(undefined);
    } else if (isMounted.current && chosenCategoriesNames.length === 0) {
      setErrorMessage("No data to compare");
    }
    if (response && isMounted.current) {
      setGraphData({
        ...graphData,
        series: [chosenMonthBudget, previousMonthBudget],
        options: {
          ...graphData.options,
          xaxis: {
            categories: chosenCategoriesNames
          }
        }
      });
    }
  };
  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => {
      isMounted.current = false;
    };
  }, [date]);

  useEffect(() => {
    if (tierBudgetLoading === false) {
      const datePicker = document.getElementById("datePickerTierBudget");
      if (datePicker) {
        datePicker.setAttribute("readOnly", true);
      }
    }
  }, [tierBudgetLoading]);

  return (
    <TierBudgetStyles>
      <Card
        boxShadow="
				0 2px 4px -1px rgba(0, 0, 0, 0.2),
				0 1px 10px 0 rgba(0, 0, 0, 0.12),
				0 4px 5px 0 rgba(0, 0, 0, 0.14)
			"
      >
        <div className="titleContainer">
          <DatePicker
            className="datePickerInput"
            id="datePickerTierBudget"
            selected={date}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            onChange={chosenDate => handleDateChange(chosenDate)}
            value={date}
            maxDate={new Date()}
          />
          <h3 className="title">{t("budget_spent_tiers")}</h3>
        </div>
        <div
          className="chartAndMessageContainer"
          onClick={e => e.stopPropagation()}
        >
          {errorMessage && (
            <div className="messageContainer">
              <p className="text">{errorMessage}</p>
            </div>
          )}
          <ReactApexChart
            options={graphData.options}
            series={graphData.series}
            type="bar"
            width={580}
            height={285}
          />
        </div>
      </Card>
    </TierBudgetStyles>
  );
};

TierBudget.propTypes = {
  tierBudgetLoading: PropTypes.bool,
  setTierBudgetLoading: PropTypes.func
};

export default TierBudget;
